import { useReducer, useEffect } from "react";
import Auth from '@aws-amplify/auth';
import { API_URL } from '../global'

/** In JavaScript, the fundamental way that we group and pass 
 * around data is through objects. 
 * In TypeScript, we represent those through object types.
 * With the alias type.
 */

// For every object in typescript, we need to declare an interface.

// These are the specifications of the state. State is an object with properties.
type useFetchData<Type> = {
    data: Type,
    loaded: boolean,
    error: string
};
// These are the specifications of the action..
type useFetchReducerAction = { // 
    type: string,
    data?: any,                 // Properties with question marks are optional.
    error?: string
};

// Reducer function, responsible for updating the state.
// Accepts state and actions as parameters, and based on action type it returns something.
// We need to specify the type of the state and action.
const useFetchReducer = <Type>(state: useFetchData<Type>, action: useFetchReducerAction) => {
    switch (action.type) {
        case 'uFStarted':
            return { ...state, loaded: false, error: null, data: null };
        case 'uFSuccess':
            return { ...state, loaded: true, error: null, data: action.data };
        case 'uFFailed':
            return { ...state, loaded: false, error: action.error, data: null };
        default:
            throw new Error('uknown action type: ' + action.type);
    }
};

type useFetchParams = {
    do_load?: boolean,
    auth?: boolean,
    extract?: (data: any) => any, // Any data type, we can assign a value of any type to the variable.
    api?: string
};

// Hook to fetch data from API.
// :returns state object with `{data: <any>, loaded: <bool>, error: <any> }`,
//   where `data` and `error` use `null` as null-value.
// The hook takes care of a clean up when component is unmounted, or reload
// is needed. In this case, slow fetch() calls are killed.
//
// To reload the data fetched, the URL has to change; this can be achieved by simply
// appending a bogus random query parameter to the URL.
//
// the options object may contain the following:
// - do_load - boolean, if present and false, the fetch() is not actually triggered,
//   and state object contains `{data: null, loaded: false, error: null }`,
//   with no updates being done
// - auth - boolean, to include Authorization header, default is `true`
// - extract - function `(data) => {... }` to process the response
// - api - base URL of API, if it differs from the default in global.api_url

const useFetch = <Type = any>(url: string, { do_load = true, auth = true, extract = null, api = null }: useFetchParams = {}): useFetchData<Type> => {
    // we're using useReducer() rather than useState() here, as useState()
    // may suffer from race conditions on fast updates, while useReducer() does not
    // We call the useReducer hook and we parse in it's function and the useFetchData object which returns state and dispatch for use in our component.
    // Depending on the action object, reducer must update the state and return a state.
    const [state, dispatch] = useReducer(useFetchReducer, { data: null, loaded: false, error: null });
    /** The useReducer uses dispatch instead of setState.
 * useReducer is also the way of managing the state in React.
 * */

    useEffect(() => {
        // signal object to kill async fetch request on clean up
        const abortCtrl = new AbortController();

        // we cannot async within useEffect(), so just define and call it right away
        const doFetch = async () => {
            if (do_load) {

                dispatch({ type: 'uFStarted' });
                // load the options

                const full_url = (api || API_URL) + url;

                var fetch_opts: RequestInit = {
                    mode: 'cors',
                    signal: abortCtrl.signal,
                    headers: {}
                };

                if (auth) {
                    const session = await Auth.currentSession();
                    fetch_opts.headers = { 'Authorization': session.getIdToken().getJwtToken() };
                }

                try {
                    const response = await fetch(full_url, fetch_opts);
                    //console.log(response)
                    if (!response.ok)
                        throw new Error(`${response.status} ${response.statusText}`);

                    const data = await response.json();

                    dispatch({ type: 'uFSuccess', data: extract ? extract(data) : data });
                } catch (e) {
                    // aborted fetch actually throws, so make sure this is not the case...
                    if (!abortCtrl.signal.aborted)
                        dispatch({ type: 'uFFailed', error: e.message });
                }
            } else {
                // no load, no nothing for ya...
            }
        };
        doFetch();

        // clean up function that aborts the fetch call if it's taking too long...
        return () => { abortCtrl.abort() };
    }, [url]);

    return state;
};

export default useFetch;

/** If you assign null to a variable, you might get a 
 * "type 'null' is not assignable to type 'string'".
 * This happens because strictNullChecks is ON. This wouldn't happen if it was OFF.
 * We turn it OFF by using the "!".
 */

